import { useCallback, useEffect, useState } from "react";

/**
 * Shared PWA install controller. Wraps the ``beforeinstallprompt`` event
 * so multiple entry points (the floating chip, the mobile-app section
 * button, an eventual navbar link, etc.) all read from a single source
 * of truth and never each install their own listener that fires only
 * for whoever mounted first.
 *
 * Also folds the iOS Safari + iOS Chrome (CriOS) flows in: since those
 * browsers never fire ``beforeinstallprompt``, we can't natively prompt.
 * Instead ``install()`` dispatches a window CustomEvent that the
 * platform-specific hint modals (``IosInstallHint``,
 * ``ChromeIosInstallHint``) listen for and force-show — reusing exactly
 * the same instructions they already surface unprompted.
 */

export const INSTALL_HINT_EVENT = "cv:show-install-hint";

function detectPlatform() {
  if (typeof window === "undefined") return "other";
  const ua = window.navigator.userAgent || "";
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (standalone) return "installed";
  const iOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (ua.includes("Macintosh") && "ontouchend" in document);
  if (iOS) {
    if (/CriOS/i.test(ua)) return "ios-chrome";
    // In-app browsers (Instagram, FB, TikTok, etc.) can't A2HS either;
    // treat them as ios-chrome so we surface the CriOS-style modal
    // guiding the viewer to open in Safari.
    if (/FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line|TikTok/i.test(ua))
      return "ios-chrome";
    return "ios-safari";
  }
  return "other"; // desktop / Android — beforeinstallprompt handles it
}

export function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [platform, setPlatform] = useState("other");

  useEffect(() => {
    setPlatform(detectPlatform());
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setPlatform("installed");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    // Preferred path: browser gave us a deferred install event.
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          setDeferred(null);
          return "accepted";
        }
        return "dismissed";
      } catch {
        return "error";
      }
    }
    // iOS fallbacks — surface the existing platform-specific hint modal.
    if (platform === "ios-safari" || platform === "ios-chrome") {
      window.dispatchEvent(
        new CustomEvent(INSTALL_HINT_EVENT, { detail: { platform } })
      );
      return "hint-shown";
    }
    return "unavailable";
  }, [deferred, platform]);

  return {
    // True when the button can do *something* useful — either fire the
    // native prompt, or open the iOS hint modal.
    canInstall: !!deferred || platform === "ios-safari" || platform === "ios-chrome",
    isStandalone: platform === "installed",
    platform,
    install,
  };
}
